import { autoUpdate, flip, offset, shift, size, useDismiss, useFloating, useInteractions } from '@floating-ui/react';
import PropTypes from 'prop-types';

import { useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import Checkbox from 'components/global/Checkbox';
import Icon from 'components/global/Icon';
import ReactPortal from 'components/global/ReactPortal';
import Tooltip from 'components/global/Tooltip';
import Typography from 'components/global/Typography';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useDebounce from 'hooks/useDebounce';
import useMediaQuery from 'hooks/useMediaQuery';
import useUpdateEffect from 'hooks/useUpdateEffect';

import { FORM } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { Breakpoints, POPPER_CONFIG } from 'utils/const.styles';

import { faCircleNotch, faExclamationCircle, faSearch, faTimes, faXmark } from '@fortawesome/pro-solid-svg-icons';

import Button from './Button';
import ViewField from './ViewField';
import * as S from './styled/AutoComplete.styled';
import { InputMessageContainer } from './styled/InputCommon.styled';

const AutoComplete = (props) => {
	const {
		name = '',
		autoCompleteName,
		filterObject,
		columns,
		selectionLabelAccessor,
		value,
		setValue,
		textValue,
		setTextValue,
		placeholder = 'search',
		error,
		disabled,
		multipleSelection,
		viewMode,
		firstItem,
		className,
		valueRemovable = true,
		fixedContainerWidth = true,
		errorMessage: errorMessageProp,
		customValue,
		fetchFunction
	} = props;

	const formMethods = useFormContext();
	const errorMessage = formMethods?.getFieldState?.(name, formMethods?.formState)?.error?.message ?? errorMessageProp;

	const { t } = useTranslation();
	const { showError } = useAlert();
	const axiosPrivate = useAxiosPrivate();

	const isFetchMode = fetchFunction !== undefined;
	const [inputValue, setInputValue] = useState(textValue ?? '');
	const debouncedInputValue = useDebounce(inputValue);
	const [open, setOpen] = useState(false);
	const [selectedData, setSelectedData] = useState(Array.isArray(value) && value.length ? value : null);
	const [localPlaceholder, setLocalPlaceholder] = useState(placeholder);
	const [keySelectedItem, setKeySelectedItem] = useState(-1); // this is used to navigate through the list with keyboard, -1 means no item is selected

	const [isFirstRender, setIsFirstRender] = useState(true);
	const menuRef = useRef(null);
	const inputRef = useRef(null);
	const isMobile = useMediaQuery(Breakpoints.mobile);

	const middleware = useMemo(
		() => [
			flip({
				padding: 10
			}),
			shift({
				mainAxis: false
			}),
			offset({
				mainAxis: 8,
				crossAxis: 0
			}),
			size({
				apply({ availableHeight, rects, elements, x }) {
					if (fixedContainerWidth) {
						elements.floating.style.width = `${rects.reference.width}px`;
					} else if (isMobile) {
						const popperWidth = document.documentElement.clientWidth - 2 * POPPER_CONFIG.MARGIN;
						elements.floating.style.width = `${popperWidth}px`;
						elements.floating.style.left = `${POPPER_CONFIG.MARGIN - x}px`;
					}
					elements.floating.style.maxHeight = availableHeight
						? `min(33rem, calc(${availableHeight}px - 5.5rem))`
						: '33rem';
				}
			})
		],
		[fixedContainerWidth, isMobile]
	);

	const { refs, context, floatingStyles } = useFloating({
		placement: 'bottom-start',
		open,
		onOpenChange: setOpen,
		middleware,
		whileElementsMounted: autoUpdate
	});

	const closePopper = () => {
		if (!open) return;
		setKeySelectedItem(-1);
		setOpen(false);
	};

	const handleClickOutside = () => {
		if (!open) return;
		closePopper();

		if (multipleSelection) {
			setInputValue('');
		} else {
			if (selectedData) {
				setInputValue(getLabel(selectedData));
			} else if (!setTextValue) {
				setInputValue('');
			}
		}
	};

	const dismiss = useDismiss(context, {
		outsidePress: handleClickOutside
	});
	const { getReferenceProps, getFloatingProps } = useInteractions([dismiss]);

	const handleFocus = () => {
		inputRef.current.focus();
	};

	const handleInputChange = (e) => {
		setInputValue(e.target.value);
		setOpen(true);
	};

	const handleItemClick = (item) => {
		setSelectedData(item);
		setOpen(false);
	};

	const handleCheckboxChange = (item) => {
		if (selectedData) {
			if (!selectedData.find(({ ID }) => ID === item.ID)) {
				setSelectedData([...selectedData, item]);
			} else {
				const filteredData = selectedData.filter(({ ID }) => ID !== item.ID);
				setSelectedData(filteredData.length ? filteredData : null);
			}
		} else {
			setSelectedData([item]);
		}

		handleFocus();
	};

	const handleSingleDelete = (e) => {
		e.stopPropagation();

		setSelectedData(null);
		setInputValue('');
	};

	const handleDelete = (e, item) => {
		e.stopPropagation();

		const filteredData = selectedData.filter(({ ID }) => ID !== item.ID);
		setSelectedData(filteredData.length ? filteredData : null);
	};

	const fetchData = async (setData, setLoading, searchID, searchMode) => {
		setLoading && setLoading(true);

		const keywordParam =
			!multipleSelection && debouncedInputValue === getLabel(selectedData) ? undefined : debouncedInputValue;

		if (isFetchMode) {
			if (multipleSelection || searchMode) {
				const data = await fetchFunction({ keyword: keywordParam });
				setData(data ?? null);
			} else if (searchID) {
				if (!Array.isArray(searchID)) {
					throw new Error('Autocomplete: Search ID must be an array');
				}
				const filteredData = await fetchFunction({ IDs: searchID });
				setData(filteredData[0] ?? null);
			} else {
				throw new Error('Autocomplete: fetchFunction must be defined when searchID is null');
			}

			setLoading && setLoading(false);
			return;
		}

		const requestJson = {
			IDs: searchID,
			keyword: searchID ? undefined : keywordParam,
			selectedIDs: searchID ? undefined : multipleSelection ? selectedData?.map(({ ID }) => ID) : undefined,
			...filterObject
		};

		await axiosPrivate
			.post(`${FORM.GET_AUTOCOMPLETE}/${autoCompleteName}`, requestJson)
			.then((response) => {
				if (response.status === 200) {
					setData(
						multipleSelection || searchMode
							? searchID
								? response.data.sort((a, b) => searchID.indexOf(a.ID) - searchID.indexOf(b.ID))
								: (response.data ?? null)
							: (response.data[0] ?? null)
					);
				} else {
					AppLogger.error(response);
				}
			})
			.catch((error) => {
				AppLogger.error(error);
				showError({
					header: 'formFieldServiceError.header',
					message: error.message
				});
			});

		setLoading && setLoading(false);
	};

	useUpdateEffect(() => {
		if (setTextValue && !selectedData) {
			setTextValue(inputValue);
		}
	}, [inputValue]);

	const getLabel = (value) => value && selectionLabelAccessor.map((item) => value[item]).join(', ');

	useEffect(() => {
		if (multipleSelection) {
			if (JSON.stringify(selectedData?.map(({ ID }) => ID)) === JSON.stringify(value)) {
				return;
			}
		} else {
			if (value === selectedData?.ID || (value === null && selectedData === null)) {
				setIsFirstRender(false);
				return;
			}
		}

		if (value === null) {
			setSelectedData(null);
		} else if (multipleSelection && value?.length) {
			fetchData(setSelectedData, null, value);
		} else if (!multipleSelection && value) {
			fetchData(setSelectedData, null, [value]);
		}
	}, [value, multipleSelection]);

	useUpdateEffect(() => {
		setValue && setValue(selectedData, isFirstRender);
		setIsFirstRender(false);

		if (multipleSelection) {
			setLocalPlaceholder(selectedData?.length ? undefined : placeholder);
			setInputValue('');
		} else {
			setInputValue(selectedData ? getLabel(selectedData) : '');
		}
	}, [selectedData]);

	const handleOnFocus = () => {
		if (open) return;
		setOpen(true);
	};

	const handleKeyDown = (e) => {
		if (multipleSelection && e.key === 'Backspace' && !inputValue && selectedData?.length) {
			const copyData = [...selectedData];
			copyData.pop();
			setSelectedData(copyData);
			handleFocus();
		}

		if (e.key === 'Enter') {
			e.preventDefault();

			if (open) {
				const currentItem = refs.floating.current.children[keySelectedItem];
				if (currentItem) {
					currentItem.click();
				}
			} else {
				setOpen(true);
			}
			setKeySelectedItem(-1);
		}

		if (!open) {
			return;
		}

		if (e.key === 'Escape') {
			closePopper();
		}

		if (e.key === 'ArrowDown') {
			e.preventDefault();
			if (keySelectedItem < refs.floating.current.children.length - 1) {
				setKeySelectedItem(keySelectedItem + 1);
			} else {
				return;
			}

			const currentItem = refs.floating.current.children[keySelectedItem];
			if (currentItem) {
				currentItem.classList.remove('focus');
			}
			refs.floating.current.children[keySelectedItem + 1].classList.add('focus');
			refs.floating.current.children[keySelectedItem + 1].scrollIntoView({
				behavior: 'smooth',
				block: 'center'
			});
		}

		if (e.key === 'ArrowUp') {
			e.preventDefault();
			if (keySelectedItem >= 1) {
				setKeySelectedItem(keySelectedItem - 1);
			} else {
				return;
			}

			const currentItem = refs.floating.current.children[keySelectedItem];
			if (currentItem) {
				currentItem.classList.remove('focus');
			}
			refs.floating.current.children[keySelectedItem - 1].classList.add('focus');
			refs.floating.current.children[keySelectedItem - 1].scrollIntoView({
				behavior: 'smooth',
				block: 'center'
			});
		}
	};

	const showSearchIcon =
		!(multipleSelection ? selectedData && selectedData.length > 0 : !!selectedData) && !customValue;

	return (
		<>
			<S.AutoCompleteContainer
				ref={menuRef}
				className={`autocomplete ${errorMessage ? 'error' : ''} ${className ?? ''} ${
					name ? `autocomplete-class-selector-${name}` : ''
				}`}
				viewmode={viewMode || disabled}>
				{viewMode || disabled ? (
					<ViewField
						value={
							multipleSelection
								? selectedData
									? selectedData.map((val) => t(getLabel(val))).join(', ')
									: ''
								: inputValue
									? inputValue
									: '-'
						}
						disabled={disabled}
					/>
				) : (
					<>
						<S.AutoCompleteInputWrapper>
							<S.AutoCompleteInputContainer ref={refs.setReference} {...getReferenceProps()}>
								{showSearchIcon && (
									<S.SearchIcon>
										<Icon icon={faSearch} />
									</S.SearchIcon>
								)}
								{multipleSelection ? (
									<S.Wrapper
										isSelected={!showSearchIcon}
										onClick={handleFocus}
										className={`${errorMessage || error ? 'error' : ''}`}>
										{selectedData &&
											[...selectedData].map((item, index) => (
												<S.Token key={index}>
													<Typography>{getLabel(item)}</Typography>
													<Button
														variant="borderless"
														iconButton
														size="small"
														iconSize="small"
														icon={faXmark}
														onClick={(e) => handleDelete(e, item)}
													/>
												</S.Token>
											))}
										<S.InputToken>
											<S.Input
												autoComplete="off"
												ref={inputRef}
												type="text"
												placeholder={localPlaceholder && t(localPlaceholder)}
												value={inputValue || ''}
												onKeyDown={handleKeyDown}
												onChange={handleInputChange}
												onFocus={handleOnFocus}
											/>
										</S.InputToken>
									</S.Wrapper>
								) : (
									<>
										<S.AutoCompleteInput
											autoComplete="off"
											ref={inputRef}
											type="text"
											className={`${errorMessage || error ? 'error' : ''}`}
											placeholder={localPlaceholder && t(localPlaceholder)}
											value={
												customValue
													? open
														? (inputValue ?? '')
														: t(customValue)
													: (inputValue ?? '')
											}
											onKeyDown={handleKeyDown}
											onChange={handleInputChange}
											onFocus={handleOnFocus}
											isSelected={!showSearchIcon}
										/>
										{inputValue && valueRemovable && (
											<S.DeleteIconContainer>
												<Icon icon={faTimes} onClick={(e) => handleSingleDelete(e)} />
											</S.DeleteIconContainer>
										)}
									</>
								)}
							</S.AutoCompleteInputContainer>
						</S.AutoCompleteInputWrapper>
						{open && (
							<ReactPortal wrapperId={'autocomplete-portal'}>
								<AutoCompletePanel
									panelRef={refs.setFloating}
									panelStyle={floatingStyles}
									searchKey={debouncedInputValue}
									handleItemClick={handleItemClick}
									columns={columns}
									value={value}
									multipleSelection={multipleSelection}
									handleCheckboxChange={handleCheckboxChange}
									fetchData={fetchData}
									firstItem={firstItem}
									closePopper={closePopper}
									isFetchMode={isFetchMode}
									getFloatingProps={getFloatingProps()}
								/>
							</ReactPortal>
						)}
					</>
				)}
			</S.AutoCompleteContainer>
			{errorMessage && (
				<InputMessageContainer className="error-message-container autocomplete-error-message-container">
					<Icon icon={faExclamationCircle} color="var(--red)" size="x-small" />
					<Typography variant="caption" color="var(--red)" noWrap>
						{errorMessage}
					</Typography>
				</InputMessageContainer>
			)}
		</>
	);
};

const AutoCompletePanel = (props) => {
	const {
		panelRef,
		panelStyle,
		searchKey,
		handleItemClick,
		columns,
		value,
		multipleSelection,
		handleCheckboxChange,
		fetchData,
		firstItem,
		closePopper,
		isFetchMode,
		getFloatingProps
	} = props;

	const [data, setData] = useState([]);
	const [dataLength, setDataLength] = useState(0);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		fetchData(setData, setLoading, undefined, true);
	}, [searchKey]);

	const handleFirstItemClick = () => {
		firstItem?.action && firstItem?.action();
		closePopper();
	};

	useUpdateEffect(() => {
		if (data?.length > dataLength) {
			setDataLength(data.length);
		}
	}, [data]);

	const firstItemRef = useRef(null);
	const isFirstItemDisabled = useMemo(
		() => (firstItem && firstItem.disabled ? (loading ? true : firstItem.disabled(dataLength)) : false),
		[firstItem, loading, dataLength]
	);

	return (
		<S.AutoCompleteContentWrapper ref={panelRef} style={panelStyle} {...getFloatingProps}>
			{firstItem && (
				<S.AutoCompleteItem
					ref={firstItemRef}
					className={isFirstItemDisabled ? 'disabled' : ''}
					onClick={isFirstItemDisabled ? undefined : handleFirstItemClick}>
					<S.Cell>{firstItem.label}</S.Cell>
				</S.AutoCompleteItem>
			)}
			{firstItemRef && isFirstItemDisabled && (
				<Tooltip
					componentRef={firstItemRef.current}
					title={firstItem.disabledTooltip}
					placement="bottom"
					color="var(--white)"
				/>
			)}
			{loading ? (
				<S.Loading>
					<Icon icon={faCircleNotch} color="var(--blue)" className="fa-spin" />
				</S.Loading>
			) : data?.length === 0 ? (
				<S.AutoCompleteNoItems>
					<Typography color="var(--content-3)">noItems</Typography>
				</S.AutoCompleteNoItems>
			) : (
				<>
					{data?.map((item, index) => {
						if (multipleSelection) {
							const selected = Array.isArray(value) ? value.includes(item.ID) : false;
							return (
								<S.AutoCompleteItem
									key={index}
									onClick={() => handleCheckboxChange(item)}
									onMouseDown={(e) => e.stopPropagation()}
									onTouchEnd={(e) => e.stopPropagation()}
									className={selected ? 'selected' : ''}>
									<S.CheckboxCell key={`item_${index}_checkbox`}>
										<Checkbox checked={selected} readOnly />
									</S.CheckboxCell>
									{columns.map(({ render, width, accessor }, i) => (
										<S.Cell key={`item_${i}`} width={width}>
											{render ? (
												render(item[accessor])
											) : (
												<Typography>{item[accessor]}</Typography>
											)}
										</S.Cell>
									))}
								</S.AutoCompleteItem>
							);
						}
						return (
							<S.AutoCompleteItem
								key={index}
								onClick={() => handleItemClick(item)}
								onMouseDown={(e) => e.stopPropagation()}
								onTouchEnd={(e) => e.stopPropagation()}>
								{columns.map(({ render, width, accessor }, i) => (
									<S.Cell key={`item_${i}`} width={width}>
										{render ? render(item[accessor]) : <Typography>{item[accessor]}</Typography>}
									</S.Cell>
								))}
							</S.AutoCompleteItem>
						);
					})}
					{data?.length === 5 && !isFetchMode && (
						<S.AutoCompleteNoItems>
							<Typography color="var(--content-3)">searchToSeeMoreItems</Typography>
						</S.AutoCompleteNoItems>
					)}
				</>
			)}
		</S.AutoCompleteContentWrapper>
	);
};

AutoComplete.propTypes = {
	/** Name of the autocomplete. */
	autoCompleteName: PropTypes.string,
	/** Required definition array of columns. */
	columns: PropTypes.array.isRequired,
	/** Required for how to show selected item in input area with a maximum of 2 child */
	selectionLabelAccessor: PropTypes.array.isRequired,
	/** Id or id array value of the autocomplete objects. */
	value: PropTypes.any,
	/** Fired when user select an option. */
	setValue: PropTypes.func,
	/** String value of the input */
	textValue: PropTypes.string,
	/** Fired when user type something in input area. */
	setTextValue: PropTypes.func,
	/** Placeholder of the input area. */
	placeholder: PropTypes.string,
	/** If error comes true just paint the input */
	error: PropTypes.bool,
	/** Gets string for show error message. */
	errorMessage: PropTypes.string,
	/** Disable the input area. */
	disabled: PropTypes.bool,
	/** Default value is false. If set true show checkboxes for multiple selection. */
	multipleSelection: PropTypes.bool,
	/** If the view mode is true no input will show up just the string will be shown.  */
	viewMode: PropTypes.bool,
	/** html className prop of the enumselect wrapper */
	className: PropTypes.string,
	/* It is an object that has a label and an action for adding a first item to options */
	firstItem: PropTypes.exact({
		action: PropTypes.func,
		label: PropTypes.element,
		disabled: PropTypes.func,
		disabledTooltip: PropTypes.any
	}),
	/** If the value is true, the selected item can be removed. */
	valueRemovable: PropTypes.bool,
	/** Shows the given value. */
	customValue: PropTypes.string,
	/** Fetch function for getting data from server. */
	fetchFunction: PropTypes.func
};

export default AutoComplete;
